import {
  createContext,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react"

type StackEntry = {
  id: number
  content: ReactNode
  title: string | null
  open: boolean
}

type DrawerContextType = {
  open: boolean
  content: ReactNode
  title: string | null
  openDrawer: (content: ReactNode, title?: string) => void
  closeDrawer: () => void
  stack: StackEntry[]
  removeEntry: (id: number) => void
}

export const DrawerContext = createContext<DrawerContextType | undefined>(
  undefined,
)

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [stack, setStack] = useState<StackEntry[]>([])
  const nextId = useRef(0)

  const openDrawer = (content: ReactNode, title?: string) => {
    const id = nextId.current++
    setStack((prev) => [
      ...prev,
      { id, content, title: title ?? null, open: true },
    ])
  }

  const closeDrawer = () => {
    setStack((prev) => {
      if (prev.length === 0) return prev
      return prev.map((entry, index) =>
        index === prev.length - 1 ? { ...entry, open: false } : entry,
      )
    })
  }

  const removeEntry = (id: number) => {
    setStack((prev) => prev.filter((entry) => entry.id !== id))
  }

  const top = stack[stack.length - 1]

  return (
    <DrawerContext.Provider
      value={{
        open: stack.length > 0,
        content: top?.content ?? null,
        title: top?.title ?? null,
        openDrawer,
        closeDrawer,
        stack,
        removeEntry,
      }}
    >
      {children}
    </DrawerContext.Provider>
  )
}

export const useDrawer = () => {
  const ctx = useContext(DrawerContext)
  if (!ctx) throw new Error("useDrawer must be used within DrawerProvider")
  return ctx
}
